<template>
    <div class="locale-switch">
        <transition
            name="locale-accordion"
            @enter="start"
            @after-enter="end"
            @before-leave="start"
            @after-leave="end"
        >
            <div
                class="locale-switch__background"
                v-if="$localization.initialized"
                v-show="opened"
            >
                <div class="locale-switch__container">
                    <div
                        class="locale-switch__locale"
                        v-for="(localeInfo, locale) in $localization.supportedLocales"
                        :key="locale"
                        :class="{
                            'locale-switch__locale__selected': locale === $localization.locale.locale,
                            disabled: loading,
                        }"
                        role="button"
                        tabindex="0"
                        @click="onLocaleChange(locale)"
                        @keydown.space="onLocaleChange(locale)"
                        @keydown.enter="onLocaleChange(locale)"
                    >
                        <OtLocaleFlag class="locale-switch__locale__flag" :locale="localeInfo" align-left />
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script lang="ts">
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import OtLocaleFlag from './OtLocaleFlag.vue';

@Component({
    components: {
        OtLocaleFlag,
    },
})
export default class OtLocaleSwitch extends Vue {

    @Prop({ default: 'common.localization.switch.title', type: String })
        title!: string;

    @Prop({ default: 'common.localization.switch.subtitle', type: String })
        subtitle!: string;

    loading = false;

    opened = false;

    mounted(): void {
        this.$emit('opened', this.opened);
    }

    async onLocaleChange(locale: string | number) {
        if (typeof locale !== 'string') {
            // This is a quirk of vue-tsc...
            // For some reason it doesn't understand the key type of a v-for loop...
            return;
        }

        this.loading = true;

        try {
            await this.$localization.setLocale(locale);

            this.$emit('change', this.$localization.locale);
        } finally {
            this.loading = false;
            this.close();
        }
    }

    start(el: HTMLElement): void {
        el.style.height = `${el.scrollHeight}px`;
    }

    end(el: HTMLElement): void {
        el.style.height = '';
    }

    public toggle(): void {
        this.opened = !this.opened;

        this.$emit('opened', this.opened);
    }

    public open(): void {
        this.opened = true;

        this.$emit('opened', this.opened);
    }

    public close(): void {
        this.opened = false;

        this.$emit('opened', this.opened);
    }

}
</script>

<style scoped>
.locale-switch {
    width: 100%;
}

.locale-switch, .locale-switch * {
    box-sizing: border-box;
}

.locale-switch__background {
    position: relative;
    margin: 0;
    padding: 0;
    overflow: hidden;

    background: var(--ot-card-background, #ffffff);
    border-radius: var(--ot-card-border-radius, 0.75rem);
    box-shadow: var(--ot-card-shadow, 0px 12px 32px -16px rgba(30, 40, 53, 0.25));
    color: var(--ot-card-color, #000000);
}

.ot-document.is-dark .locale-switch__background {
    box-shadow: none;
    border: 2px solid var(--ot-color-core-accent-secondary, #424D5C);
    background: var(--ot-card-background, #000000);
    color: var(--ot-card-color, #ffffff);
}

.locale-switch__background.locale-accordion-enter-active,
.locale-switch__background.locale-accordion-leave-active {
    will-change: height, opacity;
    transition-property: height, opacity;
    transition-duration: var(--ot-transition-default-duration, 0.15s);
    transition-timing-function: var(--ot-transition-default-timing-function, ease-out);
    overflow: hidden;
}

.locale-switch__background.locale-accordion-enter,
.locale-switch__background.locale-accordion-leave-to {
    height: 0 !important;
    opacity: 0;
}

.locale-switch__container {
    position: relative;
    width: 100%;
    display: grid;
    grid-template-columns: repeat(2, .5fr);
    grid-auto-flow: row;
    grid-gap: 1px;

    background: var(--ot-box-accent, rgba(0,0,0,0.03));
}

.ot-document.is-dark .locale-switch__container {
    background: var(--ot-box-accent, rgba(255,255,255,0.1));
}

@media (max-width: 25rem) {
    .locale-switch__container {
        grid-template-columns: repeat(1, 1fr);
    }
}

.locale-switch__locale {
    display: block;
    padding: var(--ot-card-padding, var(--ot-spacing-lg, 1.5rem));
    background: var(--ot-card-background, #ffffff);

    cursor: pointer;
}

.locale-switch__locale__selected, .locale-switch__locale:hover {
    background-color: transparent;
}

.locale-switch__locale.disabled {
    pointer-events: none;
}
</style>
